import React, { useCallback, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ClickAwayListener, Popper, makeStyles } from '@material-ui/core'
import { ICON_NAMES } from '../../../../../../constants'
import Icon from '../../../../../atoms/Icon'
import SydInput from '../../../../../commonDesign/SydInput'

const allIconNames = Object.values(ICON_NAMES)

const useStyles = makeStyles(() => ({
  cell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    padding: '8px 4px',
    borderRadius: 'var(--border-radius-sm)',
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': {
      background: 'var(--color-neutral-100)'
    }
  },
  cellSelected: {
    background: 'var(--color-primary-100)',
    outline: '2px solid var(--color-primary-500)',
    '&:hover': {
      background: 'var(--color-primary-100)'
    }
  },
  iconLabel: {
    fontSize: '0.625rem',
    color: 'var(--text-color-subtle)',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%'
  },
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--border-radius-sm)',
    background: 'var(--color-surface-default)',
    cursor: 'pointer',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-neutral-800)',
    '&:hover': {
      borderColor: 'var(--color-primary-400)'
    }
  },
  triggerDisabled: {
    opacity: 0.5,
    pointerEvents: 'none'
  },
  popperContent: {
    width: '380px',
    background: 'var(--color-surface-default)',
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--border-radius-md)',
    boxShadow: 'var(--elevation-2, 0 4px 12px rgba(0,0,0,0.12))',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    zIndex: 1300
  },
  popperGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
    gap: '4px',
    maxHeight: '240px',
    overflowY: 'auto'
  }
}))

function CompactIconPicker ({ value, onSelect, disabled }) {
  const classes = useStyles()
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)

  const filtered = useMemo(() => {
    if (!search.trim()) return allIconNames
    const term = search.toLowerCase()
    return allIconNames.filter(name => name.toLowerCase().includes(term))
  }, [search])

  const handleSelect = useCallback((name) => {
    onSelect(name)
    setOpen(false)
  }, [onSelect])

  return (
    <>
      <button
        ref={anchorRef}
        type='button'
        className={`${classes.trigger} ${disabled ? classes.triggerDisabled : ''}`}
        onClick={() => !disabled && setOpen(prev => !prev)}
      >
        {value ? <Icon name={value} customSize={16} /> : null}
        <span>{value || 'Choose icon'}</span>
        <Icon name={ICON_NAMES.chevronDown} customSize={12} />
      </button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement='bottom-start'
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div className={classes.popperContent}>
            <SydInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search icons...'
              size='sm'
              autoFocus
            />
            <div className={classes.popperGrid}>
              {filtered.map(name => (
                <div
                  key={name}
                  className={`${classes.cell} ${value === name ? classes.cellSelected : ''}`}
                  onClick={() => handleSelect(name)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleSelect(name)
                    }
                  }}
                  title={name}
                >
                  <Icon name={name} customSize='24px' />
                  <span className={classes.iconLabel}>{name}</span>
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '16px', color: 'var(--text-color-subtle)', fontSize: '0.875rem' }}>
                  No icons match &quot;{search}&quot;
                </div>
              )}
            </div>
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  )
}

CompactIconPicker.propTypes = {
  value: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

export default CompactIconPicker
